import { fetchWithAuth } from "./fetchWithAuth";

const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const HttpService = {
  async fetch(url: string, options: RequestInit = {}) {
    try {
      const response = await this.fetchResponse(url, options);
      return response.json();
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  },
  async fetchBlob(url: string, options: RequestInit = {}) {
    try {
      const response = await this.fetchResponse(url, options);
      return response.blob();
    } catch (error) {
      console.error("Error fetching blob:", error);
      throw error;
    }
  },
  async fetchResponse(url: string, options: RequestInit = {}) {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No access token found");
      }

      const headers: HeadersInit = new Headers();
      headers.set("authorization", `Bearer ${token}`);

      if (!(options.body instanceof FormData)) {
        headers.set("Content-Type", "application/json");
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error("Error fetching response:", error);
      throw error;
    }
  },

  async getFile(
    containerName: string,
    ID: string,
    fileName: string,
  ): Promise<string> {
    if (!apiBaseUrl) {
      throw new Error("API URL is not defined");
    }

    let response;
    if (containerName === "signatures") {
      const encodedFileName = encodeURIComponent(fileName);
      response = await fetch(
        `${apiBaseUrl}/api/Blob/GetBlobSignatures/${ID}/${encodedFileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    } else if (containerName === "reference-screening-authorizations") {
      const encodedFileName = encodeURIComponent(fileName);
      response = await fetch(
        `${apiBaseUrl}/api/Blob/GetBlobAuthForm/${ID}/${encodedFileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    } else if (containerName === "page-decorations") {
      const encodedFileName = encodeURIComponent(fileName);
      response = await fetch(
        `${apiBaseUrl}/api/Blob/GetBlobPageDecorationForm/${ID}/${encodedFileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    } else {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("User is not authenticated");
      }
      const encodedFileName = encodeURIComponent(fileName);
      response = await fetch(
        `${apiBaseUrl}/api/Blob/GetBlob/${containerName}/${ID}/${encodedFileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch file for ClientID ${ID}.`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    return objectUrl;
  },

  async getFileAdmin(
    containerName: string,
    ID: string,
    fileName: string,
  ): Promise<string> {
    if (!apiBaseUrl) {
      throw new Error("API URL is not defined");
    }

    let response;
    if (containerName === "signatures") {
      const encodedFileName = encodeURIComponent(fileName);
      response = await fetchWithAuth(
        `${apiBaseUrl}/api/Blob/GetBlobSignatures/${ID}/${encodedFileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    } else if (containerName === "reference-screening-authorizations") {
      const encodedFileName = encodeURIComponent(fileName);
      response = await fetchWithAuth(
        `${apiBaseUrl}/api/Blob/GetBlobAuthForm/${ID}/${encodedFileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    } else if (containerName === "page-decorations") {
      const encodedFileName = encodeURIComponent(fileName);
      response = await fetchWithAuth(
        `${apiBaseUrl}/api/Blob/GetBlobPageDecorationForm/${ID}/${encodedFileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    } else {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("User is not authenticated");
      }
      const encodedFileName = encodeURIComponent(fileName);
      response = await fetchWithAuth(
        `${apiBaseUrl}/api/Blob/GetBlob/${containerName}/${ID}/${encodedFileName}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch file for ClientID ${ID}.`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    return objectUrl;
  },

  async logFrontendError(error: any, context: FrontendErrorContext = {}) {
    try {
      const payload = {
        message: error?.message || "Unknown front-end error",
        stack: error?.stack || "Unable to grab stack trace",
        name: error?.name || "Error",
        context: {
          ...context,
          url: window.location.href,
          userAgent: navigator.userAgent,
        },
      };

      await fetch(`${apiBaseUrl}/api/Auth/save-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("Failed to log frontend error:", err);
    }
  },
};

type FrontendErrorContext = {
  flow?: "msal" | "auth0";
  stage?: string;
  email?: string;
  portal?: string;
  attemptID?: string;
};

export const deleteFile = async (
  containerName: string,
  ID: number,
  fileName: string,
): Promise<boolean> => {
  try {
    const urlFile = encodeURIComponent(fileName);
    const token = localStorage.getItem("token");
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/Blob/DeleteFile/${containerName}/${ID}/${urlFile}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.ok;
  } catch (error) {
    console.error("Delete form error:", error);
    return false;
  }
};
